<template>
  <div>
    <div class="flex justify-between items-center p-5">
      <div class="flex items-center">
        <img v-if="file.iconUrl && file.type !== ListingItemType.LINK" :src="file.iconUrl" alt="Icon Image" class="w-10">
        <div v-else class="w-10 flex justify-center">
          <d-icon :icon="computedIcon" class="text-4xl" />
        </div>
        <div class="mx-5">
          <p class="font-bold">
            {{ file.name }}
          </p>
          <p v-if="!canEdit" class="text-sm">
            {{ formatDate(file.createdAt) }}
          </p>
          <div v-else class="flex ">
            <p class="text-sm">
              <a href="javascript:;" class="underline" @click.stop="launchEditModal">Edit Details</a>
            </p>
            <p v-if="file.type === ListingItemType.QUIZ" class="text-sm ml-3">
              <a href="javascript:;" class="underline" @click="launchQuestionListModal">Add/Edit Questions</a>
            </p>
            <p v-if="file.type === ListingItemType.EXAM" class="text-sm ml-3">
              <a href="javascript:;" class="underline" @click="launchExamQuestionListModal">Add/Edit Questions</a>
            </p>
          </div>
        </div>
      </div>
      <d-icon icon="trash" class="text-error cursor-pointer" @click.stop="removeItem" />
    </div>

    <!-- Question List Modal -->
    <QuestionListModal
      :active="showQuestionListModal"
      :item="file"
      :content-listing="contentListing"
      @close="closeQuestionListModal"
    />

    <!-- Exam Question List Modal -->
    <ExamQuestionListModal
      :active="showExamQuestionListModal"
      :exam="file"
      :content-listing="contentListing"
      @close="closeExamQuestionListModal"
    />

    <!-- Edit Details Modals -->
    <d-modal
      :active="showFolderModal"
      :width="850"
      @close="closeFolderModal"
      :can-cancel="['escape', 'x', 'outside']"
    >
      <FolderModal
        :file="file"
        :content-listing="contentListing"
        @close="closeFolderModal"
        @saved="handleModalSaved"
      />
    </d-modal>

    <d-modal
      :active="showLinkModal"
      :width="850"
      @close="closeLinkModal"
      :can-cancel="['escape', 'x', 'outside']"
    >
      <LinkModal
        :file="file"
        :content-listing="contentListing"
        @close="closeLinkModal"
        @saved="handleModalSaved"
      />
    </d-modal>

    <d-modal
      :active="showHtmlModal"
      :width="850"
      @close="closeHtmlModal"
      :can-cancel="['escape', 'x', 'outside']"
    >
      <HtmlModal
        :file="file"
        :content-listing="contentListing"
        @close="closeHtmlModal"
        @saved="handleModalSaved"
      />
    </d-modal>

    <d-modal
      :active="showQuizModal"
      :width="850"
      @close="closeQuizModal"
      :can-cancel="['escape', 'x', 'outside']"
    >
      <QuizModal
        :file="file"
        :content-listing="contentListing"
        @close="closeQuizModal"
        @saved="handleModalSaved"
      />
    </d-modal>

    <d-modal
      :active="showExamModal"
      :width="850"
      @close="closeExamModal"
      :can-cancel="['escape', 'x', 'outside']"
    >
      <ExamModal
        :exam="file"
        :content-listing="contentListing"
        @close="closeExamModal"
        @saved="handleModalSaved"
      />
    </d-modal>

    <d-modal
      :active="showEditDetailsModal"
      :width="850"
      @close="closeEditDetailsModal"
      :can-cancel="['escape', 'x', 'outside']"
    >
      <EditDetailsModal
        :file="file"
        :content-listing="contentListing"
        @close="closeEditDetailsModal"
        @saved="handleModalSaved"
      />
    </d-modal>
  </div>
</template>

<script>
  import { deleteItem } from '@resources/scripts/services/contentListingItemsApi.service';
  import { ListingItemType } from '@resources/scripts/constants';
  import FolderModal from '../Organization/FolderModal.vue';
  import LinkModal from '../Organization/LinkModal.vue';
  import HtmlModal from '../Organization/HtmlModal.vue';
  import EditDetailsModal from '../Organization/EditDetailsModal.vue';
  import QuizModal from '../Organization/QuizModal.vue';
  import ExamModal from '../Organization/ExamModal.vue';
  import QuestionListModal from '../Organization/QuestionListModal.vue';
  import ExamQuestionListModal from '../Organization/ExamQuestionListModal.vue';

  export default {
    components: {
      QuestionListModal,
      ExamQuestionListModal,
      FolderModal,
      LinkModal,
      HtmlModal,
      EditDetailsModal,
      QuizModal,
      ExamModal,
    },

    inject: ['getFiles', 'contentListing', 'saveDraggable'],
    props: {
      file: {
        type: Object,
        default: () => ({}),
      },
      canEdit: Boolean,
    },

    data() {
      return {
        ListingItemType,
        showQuestionListModal: false,
        showExamQuestionListModal: false,
        showFolderModal: false,
        showLinkModal: false,
        showHtmlModal: false,
        showQuizModal: false,
        showExamModal: false,
        showEditDetailsModal: false,
      };
    },

    computed: {
      computedIcon() {
        switch (this.file.type) {
          case ListingItemType.LINK:
            return 'link';
          case ListingItemType.EMBEDDED:
            return 'code';
          case ListingItemType.QUIZ:
            return 'question-circle';
          case ListingItemType.EXAM:
            return 'graduation-cap';
          default:
            return 'file';
        }
      },
    },

    methods: {
      async removeItem() {
        if (this.file.isExam) {
          // Handle exam deletion differently
          const { deleteExam } = await import('@resources/scripts/services/examsApi.service');
          await deleteExam(this.file.id);
        } else {
          await deleteItem(this.file.id, this.contentListing.type);
        }
        await this.getFiles();
      },

      launchEditModal() {
        switch (this.file.type) {
          case ListingItemType.FOLDER:
            this.showFolderModal = true;
            break;
          case ListingItemType.LINK:
            this.showLinkModal = true;
            break;
          case ListingItemType.EMBEDDED:
            this.showHtmlModal = true;
            break;
          case ListingItemType.QUIZ:
            this.showQuizModal = true;
            break;
          case ListingItemType.EXAM:
            this.showExamModal = true;
            break;
          default:
            this.showEditDetailsModal = true;
            break;
        }
      },

      // Modal close handlers
      closeFolderModal() {
        this.showFolderModal = false;
      },

      closeLinkModal() {
        this.showLinkModal = false;
      },

      closeHtmlModal() {
        this.showHtmlModal = false;
      },

      closeQuizModal() {
        this.showQuizModal = false;
      },

      closeExamModal() {
        this.showExamModal = false;
      },

      closeEditDetailsModal() {
        this.showEditDetailsModal = false;
      },

      async handleModalSaved() {
        // Refresh the files after a successful save
        await this.getFiles();
        // Also call saveDraggable if needed
        if (this.saveDraggable) {
          await this.saveDraggable();
        }
      },

      launchQuestionListModal() {
        this.showQuestionListModal = true;
      },

      closeQuestionListModal() {
        this.showQuestionListModal = false;
      },

      launchExamQuestionListModal() {
        this.showExamQuestionListModal = true;
      },

      closeExamQuestionListModal() {
        this.showExamQuestionListModal = false;
      },

      formatDate(date) {
        if (!date) return '';
        return new Date(date).toLocaleDateString();
      },
    },
  };
</script>